import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { Helmet } from 'react-helmet';
import LoginForm from './components/LoginForm';
import SocialAuth from './components/SocialAuth';
import AuthActions from './components/AuthActions';
import FeatureShowcase from './components/FeatureShowcase';

export default function Login() {
  const navigate = useNavigate();
  const authContext = useAuth();
  const { signIn = async () => ({ error: { message: 'Auth not available' } }) } = authContext || {};
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (email, password) => {
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await signIn(email, password);
      
      if (signInError) {
        setError(signInError?.message || 'Failed to sign in. Please check your credentials.');
        return;
      }

      // Navigate to dashboard based on user role
      if (data?.user) {
        const userRole = data?.user?.user_metadata?.role;
        
        // Check if user has completed role selection
        if (!userRole) {
          navigate('/select-role');
          return;
        }
        
        // Route based on role
        switch (userRole) {
          case 'company_admin': case'employer': navigate('/company-dashboard-hub');
            break;
          case 'admin': navigate('/admin-operations-dashboard');
            break;
          case 'job_seeker':
          default:
            navigate('/job-seeker-dashboard');
            break;
        }
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setError('');
    setLoading(true);

    try {
      // Social login will redirect to role selection if role not set
      const { data, error: oauthError } = await authService?.signInWithSocial(
        provider,
        '/auth/callback'
      );

      if (oauthError) {
        setError(oauthError?.message || `Failed to sign in with ${provider}`);
        setLoading(false);
      }
      // Note: User will be redirected to OAuth provider, loading state persists
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Recrutees</title>
      </Helmet>
      <div className="flex min-h-screen">
        {/* Left side - Login Form */}
        <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div className="mb-8">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                Sign in to your account
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Welcome back! Please enter your details.
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <LoginForm onSubmit={handleLogin} loading={loading} />
            
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">Or continue with</span>
                </div>
              </div>

              <SocialAuth onSocialLogin={handleSocialLogin} loading={loading} />
            </div>

            <AuthActions />
          </div>
        </div>

        {/* Right side - Feature Showcase */}
        <FeatureShowcase />
      </div>
    </>
  );
}